import { useEffect, useMemo, useState } from "react";
import { Polygon, Polyline } from "react-leaflet";
import { api, apiError } from "@/lib/api";
import LeafletMap, { FitBounds, GeomanEditor, DraggableControls, polygonAreaM2 } from "@/components/map/LeafletMap";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Map as MapIcon, Plus, Trash2, Pencil, X, Loader2, PencilRuler, CheckCircle2, Utensils, Bike, Coffee, Beer, Hotel, Ruler, RefreshCw, MapPin, Scissors, Spline } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { classifyBoundary } from "@/lib/geoClassify";
import BusinessLayers from "@/components/map/BusinessLayers";
import HeatLayer from "@/components/map/HeatLayer";
import { useBusinessDiscovery } from "@/lib/useBusinessDiscovery";
import { runProspectSync, syncReportText } from "@/lib/prospectSync";
import { BusinessLayerToggles } from "@/components/map/BusinessLayerToggles";
import AdminGeometryEditor from "@/components/map/AdminGeometryEditor"; // eslint-disable-line no-unused-vars
import RoutesManager from "@/components/admin/RoutesManager";
import ContentManager from "@/components/content/ContentManager";
import ZonesPanel from "@/components/admin/ZonesPanel";
import BikePathContentTab from "@/components/admin/BikePathContentTab";
import ZoneTwoLineCreate from "@/components/admin/ZoneTwoLineCreate";
import OsmAmenityLayers from "@/components/map/OsmAmenityLayers";
import { AmenityToggles } from "@/components/map/AmenityToggles";
import BikePathDuplicatesDialog from "@/components/admin/BikePathDuplicatesDialog";

const EMPTY = { id: null, name: "", description: "", boundary: [], content: {}, lines: [], centerline: [], osm_amenities: {} };
const EDITOR_TABS = [
  { key: "overview", label: "Overview", enabled: true },
  { key: "content", label: "Content & Media", enabled: true },
  { key: "stops", label: "Stops", enabled: true },
  { key: "routes", label: "Routes", enabled: true },
  { key: "businesses", label: "Businesses", enabled: true },
  { key: "assignments", label: "Assignments", enabled: true },
];

export default function BikePathsPanel() {
  const [paths, setPaths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {id,name,description,boundary}
  const [saving, setSaving] = useState(false);
  const [sortBy, setSortBy] = useState("name");
  const [fState, setFState] = useState("all");
  const [fCounty, setFCounty] = useState("all");
  const [fName, setFName] = useState("all");
  const [splitOpen, setSplitOpen] = useState(false);
  const [pathZones, setPathZones] = useState([]);
  const [needsZone, setNeedsZone] = useState(false);
  const [coverage, setCoverage] = useState(null);
  const [coverageLoading, setCoverageLoading] = useState(false);
  const toggleNeedsZone = async () => {
    if (needsZone) { setNeedsZone(false); return; }
    setCoverageLoading(true);
    try {
      const { data } = await api.get("/admin/trails/bike-paths/zone-coverage");
      setCoverage(data.coverage || {});
      setNeedsZone(true);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setCoverageLoading(false); }
  };
  const fetchPathZones = async (pid) => {
    try { const { data } = await api.get("/zones", { params: { bike_path_id: pid } }); setPathZones(data || []); }
    catch { setPathZones([]); }
  };
  const openSplit = async () => { if (editing?.id) await fetchPathZones(editing.id); setSplitOpen(true); };

  const withGeo = useMemo(() => paths.map((p) => ({ ...p, geo: classifyBoundary(p.boundary || []) })), [paths]);
  // Harvested trail route (LineString/MultiLineString) saved alongside the boundary.
  const routeLines = useMemo(() => {
    if (editing?.lines?.length) return editing.lines.filter((ln) => ln && ln.length >= 2);
    if (editing?.centerline?.length >= 2) return [editing.centerline];
    return [];
  }, [editing]);
  const stateOptions = useMemo(() => Array.from(new Set(withGeo.map((p) => p.geo.state).filter(Boolean))).sort(), [withGeo]);
  const countyOptions = useMemo(() => (fState === "all" ? [] : Array.from(new Set(withGeo.filter((p) => p.geo.state === fState).map((p) => p.geo.county).filter(Boolean))).sort()), [withGeo, fState]);
  const nameOptions = useMemo(() => withGeo
    .filter((p) => (fState === "all" || p.geo.state === fState) && (fCounty === "all" || p.geo.county === fCounty))
    .map((p) => ({ id: p.id, name: p.name }))
    .sort((a, b) => a.name.localeCompare(b.name)), [withGeo, fState, fCounty]);

  const filtered = useMemo(() => withGeo.filter((p) => {
    if (fState !== "all" && p.geo.state !== fState) return false;
    if (fCounty !== "all" && p.geo.county !== fCounty) return false;
    if (fName !== "all" && p.id !== fName) return false;
    if (needsZone && coverage && (coverage[p.id] ?? 0) >= 50) return false;
    return true;
  }), [withGeo, fState, fCounty, fName, needsZone, coverage]);

  const sorted = useMemo(() => {
    const bm = (p) => p.business_metrics || {};
    const keyer = {
      name: (p) => (p.name || "").toLowerCase(),
      restaurants: (p) => -((p.saved_counts || {}).restaurant || 0),
      restaurants_per_mile: (p) => -(bm(p).restaurants_per_mile || 0),
      bike_shops: (p) => -(bm(p).bike_shops || 0),
      bike_shops_per_mile: (p) => -(bm(p).bike_shops_per_mile || 0),
      coffee: (p) => -(bm(p).coffee_shops || 0),
      breweries: (p) => -(bm(p).breweries || 0),
      hotels: (p) => -(bm(p).hotels || 0),
    }[sortBy] || ((p) => (p.name || "").toLowerCase());
    return [...filtered].sort((a, b) => { const ka = keyer(a), kb = keyer(b); return ka < kb ? -1 : ka > kb ? 1 : 0; });
  }, [filtered, sortBy]);

  const biz = useBusinessDiscovery(editing?.boundary, { bikePathId: editing?.id });
  const [pathSync, setPathSync] = useState(null);
  const syncPathProspects = async () => {
    setPathSync({ status: "running", done: 0, total: 1 });
    try {
      const job = await runProspectSync({ bike_path_id: editing.id }, setPathSync);
      toast.success(`Prospects synced: ${syncReportText(job)}`);
      biz.refresh(); load();
    } catch (e) { setPathSync(null); toast.error(apiError(e.response?.data?.detail) || e.message); }
  };
  const [versions, setVersions] = useState([]);
  const [geomNonce, setGeomNonce] = useState(0);
  // Fit the editor map once per path-open / restore (NOT on every vertex edit).
  const initialBounds = useMemo(() => (editing?.boundary || []), [editing?.id, geomNonce]); // eslint-disable-line react-hooks/exhaustive-deps
  const [editorTab, setEditorTab] = useState("overview");

  useEffect(() => { setEditorTab("overview"); }, [editing?.id]);

  useEffect(() => {
    if (editing?.id) {
      api.get(`/bike-paths/${editing.id}/versions`).then(({ data }) => setVersions(data)).catch(() => setVersions([]));
    } else setVersions([]);
  }, [editing?.id]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bike-paths", { params: { lite: 1 } });
      setPaths(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const [dupOpen, setDupOpen] = useState(false);
  const [openingId, setOpeningId] = useState(null);
  const openEdit = async (id) => {
    setOpeningId(id);
    try {
      const { data: p } = await api.get(`/bike-paths/${id}`);
      setEditing({ id: p.id, name: p.name, description: p.description || "", boundary: p.boundary || [], content: p.content || {}, lines: p.lines || [], centerline: p.centerline || [], osm_amenities: p.osm_amenities || {} });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setOpeningId(null); }
  };

  const save = async () => {
    if (!editing.name.trim()) return toast.error("Name is required");
    if (editing.boundary.length < 3) return toast.error("Draw a boundary with at least 3 points");
    setSaving(true);
    try {
      if (editing.id) await api.put(`/bike-paths/${editing.id}`, { name: editing.name, description: editing.description, boundary: editing.boundary });
      else await api.post("/bike-paths", { name: editing.name, description: editing.description, boundary: editing.boundary });
      toast.success(editing.id ? "Bike path updated" : "Bike path created");
      setEditing(null);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/bike-paths/${id}`); toast.success("Bike path deleted"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const publish = async (id) => {
    try { await api.post(`/bike-paths/${id}/publish`); toast.success("Bike path published"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const restoreVersion = async (index) => {
    try {
      const { data } = await api.post(`/bike-paths/${editing.id}/restore`, { index });
      setEditing((s) => ({ ...s, boundary: data.boundary }));
      setGeomNonce((n) => n + 1);
      const v = await api.get(`/bike-paths/${editing.id}/versions`);
      setVersions(v.data);
      toast.success("Previous geometry restored");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const [recomputing, setRecomputing] = useState({});
  const [bulk, setBulk] = useState(null);
  // Attach-centerline flow (in-place; never touches boundary/zones/attractions/routes).
  const [attachFor, setAttachFor] = useState(null);     // the bike path being linked
  const [attachStates, setAttachStates] = useState([]);
  const [attachState, setAttachState] = useState("");
  const [attachQ, setAttachQ] = useState("");
  const [attachResults, setAttachResults] = useState([]);
  const [attachLoading, setAttachLoading] = useState(false);
  const [attaching, setAttaching] = useState(null);     // trail_id being attached
  const [refreshingAmen, setRefreshingAmen] = useState({});
  const [amenVisible, setAmenVisible] = useState({});
  const toggleAmen = (k) => setAmenVisible((s) => ({ ...s, [k]: !s[k] }));
  const refreshAmenities = async (id) => {
    setRefreshingAmen((r) => ({ ...r, [id]: true }));
    try {
      const { data } = await api.post(`/admin/trails/bike-paths/${id}/refresh-amenities`);
      toast.success(`Amenities refreshed — ${(data.parks || []).length} parks · ${(data.bathrooms || []).length} restrooms · ${(data.parking || []).length} parking`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setRefreshingAmen((r) => ({ ...r, [id]: false })); }
  };

  const openAttach = async (p) => {
    setAttachFor(p);
    setAttachResults([]); setAttachQ("");
    const guess = (p.geo?.state && p.geo.state.length === 2) ? p.geo.state : "";
    setAttachState(guess);
    try {
      const { data } = await api.get("/admin/trails/states");
      const avail = (data.states || []).filter((s) => s.available > 0);
      setAttachStates(avail);
    } catch { setAttachStates([]); }
  };
  const searchTrails = async () => {
    if (!attachState) return toast.error("Pick a state first");
    setAttachLoading(true);
    try {
      const { data } = await api.get("/admin/trails", { params: { state: attachState, q: attachQ.trim() || undefined, min_miles: 0, hide_numbered: false, limit: 50 } });
      setAttachResults(data.items || []);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setAttachLoading(false); }
  };
  const doAttach = async (trailId) => {
    setAttaching(trailId);
    try {
      await api.post(`/admin/trails/${trailId}/attach-centerline`, { bike_path_id: attachFor.id });
      toast.success("Centerline attached — the trail now shows a blue line on the rider map. Boundary, zones, attractions and routes are unchanged.");
      setAttachFor(null);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setAttaching(null); }
  };
  const recomputeAll = async () => {
    try {
      await api.post(`/admin/trails/bike-paths/recompute-all`);
      setBulk({ status: "running", total: 0, done: 0 });
      const poll = setInterval(async () => {
        try {
          const { data } = await api.get(`/admin/trails/bike-paths/recompute-all/status`);
          setBulk(data);
          if (data.status === "done" || data.status === "error") {
            clearInterval(poll);
            if (data.status === "error") toast.error("Bulk recompute failed: " + (data.error || "unknown"));
            else toast.success(`Recomputed ${data.done} saved path(s)`);
            load();
            setTimeout(() => setBulk(null), 4000);
          }
        } catch (e) { clearInterval(poll); setBulk(null); }
      }, 3000);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const recomputeMetrics = async (id) => {
    setRecomputing((r) => ({ ...r, [id]: true }));
    try {
      await api.post(`/admin/trails/bike-paths/${id}/recompute-metrics`);
      const poll = setInterval(async () => {
        try {
          const { data } = await api.get(`/admin/trails/bike-paths/${id}/recompute-metrics/status`);
          const st = data.metrics_recompute?.status;
          if (st === "done" || st === "error") {
            clearInterval(poll);
            setRecomputing((r) => ({ ...r, [id]: false }));
            if (st === "error") toast.error("Recompute failed: " + (data.metrics_recompute?.error || "unknown"));
            else toast.success("Business metrics recomputed");
            load();
          }
        } catch (e) { clearInterval(poll); setRecomputing((r) => ({ ...r, [id]: false })); }
      }, 2500);
    } catch (e) { setRecomputing((r) => ({ ...r, [id]: false })); toast.error(apiError(e.response?.data?.detail)); }
  };

  if (editing) {
    const pts = editing.boundary;
    return (
      <div className="space-y-4" data-testid="bikepath-editor">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold flex items-center gap-2"><MapIcon className="w-5 h-5 text-primary" />{editing.id ? "Edit bike path" : "New bike path"}</h2>
          <Button variant="ghost" size="sm" onClick={() => setEditing(null)} data-testid="bikepath-cancel"><X className="w-4 h-4 mr-1" />Cancel</Button>
        </div>
        <div className="flex flex-wrap gap-1.5 border-b border-border" data-testid="bikepath-editor-tabs">
          {EDITOR_TABS.map((t) => {
            const active = editorTab === t.key;
            const disabled = !t.enabled;
            return (
              <button key={t.key} type="button" disabled={disabled} onClick={() => t.enabled && setEditorTab(t.key)} data-testid={`bikepath-tab-${t.key}`}
                className={`px-3 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${active ? "border-primary text-primary" : "border-transparent text-muted-foreground"} ${disabled ? "opacity-40 cursor-not-allowed" : "hover:text-foreground"}`}>
                {t.label}{disabled ? " ·" : ""}
              </button>
            );
          })}
        </div>
        {editorTab === "overview" && (<>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Name</Label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="East Bay Bike Path" data-testid="bikepath-name-input" /></div>
          <div><Label>Description</Label><Input value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} placeholder="Providence → Bristol, RI" data-testid="bikepath-desc-input" /></div>
        </div>
        {editing.id && (
          <div className="flex flex-wrap items-center gap-2" data-testid="bikepath-sync-row">
            <Button variant="outline" size="sm" onClick={syncPathProspects} disabled={!!pathSync && pathSync.status === "running"} data-testid="bikepath-sync-prospects-button">
              {pathSync?.status === "running" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              {pathSync?.status === "running" ? `Syncing ${pathSync.done || 0}/${pathSync.total || 1}…` : "Sync prospects"}
            </Button>
            <span className="text-xs text-muted-foreground" data-testid="bikepath-sync-report">
              {pathSync && pathSync.status !== "running" ? `Last sync: ${syncReportText(pathSync)}` : "Finds restaurants from Google for this path's zones (or the whole corridor if it has no zones yet)."}
            </span>
          </div>
        )}
        <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 text-sm text-muted-foreground flex items-center gap-2">
          <PencilRuler className="w-4 h-4 text-primary shrink-0" />
          {editing.id
            ? "Drag any point to refine the boundary, or use the polygon tool (right side of the map) to redraw it. Drag a toolbar by its grip handle to move it. The map auto-fits when you open or restore a shape."
            : "Use the polygon tool (right side of the map) to draw the path boundary point-by-point, then drag vertices to refine. Add at least 3 points."}
        </div>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-sm text-muted-foreground" data-testid="bikepath-point-count">{pts.length} point{pts.length === 1 ? "" : "s"}{pts.length >= 3 ? ` · ~${(polygonAreaM2(pts) / 1e6).toFixed(2)} km²` : ""}</div>
          {editing.id && pts.length >= 3 && (
            <Button variant="outline" size="sm" onClick={openSplit} data-testid="bikepath-new-zone-two-line"><Scissors className="w-4 h-4 mr-1" />New zone (2 lines)</Button>
          )}
        </div>
        <div className="relative rounded-2xl overflow-hidden border border-border" data-testid="bikepath-editor-map">
          {((editing.osm_amenities?.parks || []).length + (editing.osm_amenities?.bathrooms || []).length + (editing.osm_amenities?.parking || []).length) > 0 && (
            <div className="absolute top-2 left-2 z-[1000] bg-background/90 backdrop-blur rounded-lg p-1.5 border border-border"><AmenityToggles visible={amenVisible} onToggle={toggleAmen} available={{ osm_park: (editing.osm_amenities?.parks || []).length, osm_bathroom: (editing.osm_amenities?.bathrooms || []).length, osm_parking: (editing.osm_amenities?.parking || []).length }} /></div>
          )}
          <LeafletMap height={920} center={pts.length ? pts[0] : [41.75, -71.3]} className="map-tools-right-middle" zoomPosition="topright">
            <DraggableControls />
            {initialBounds.length >= 2 && <FitBounds bounds={initialBounds} nonce={geomNonce} />}
            {routeLines.map((ln, i) => <Polyline key={`r-${i}`} positions={ln} pathOptions={{ color: "#0ea5e9", weight: 5, opacity: 0.95, interactive: false }} />)}
            <OsmAmenityLayers data={editing.osm_amenities || {}} visible={amenVisible} />
            <GeomanEditor key={`geo-${editing.id || "new"}-${geomNonce}`} polygon={editing.boundary} onChange={(b) => setEditing((s) => ({ ...s, boundary: b }))} color="#10B981" />
            {biz.visibleBiz.length > 0 && <BusinessLayers businesses={biz.visibleBiz} onShopAction={biz.handleShopAction} />}
            {biz.heatOn && <HeatLayer points={biz.heatPoints} enabled={biz.heatOn} />}
          </LeafletMap>
          {pts.length >= 3 && <BusinessLayerToggles {...biz} />}
        </div>
        {editing.id && versions.length > 0 && (
          <div className="rounded-lg border border-border bg-card p-3" data-testid="geometry-versions">
            <div className="text-xs font-semibold text-muted-foreground mb-2">Geometry history ({versions.length}) — restore a previous shape</div>
            <div className="flex flex-col gap-1 max-h-40 overflow-auto">
              {[...versions].reverse().map((v) => (
                <div key={v.index} className="flex items-center justify-between gap-2 text-xs">
                  <span className="text-muted-foreground truncate">{v.points} pts · {v.saved_at ? new Date(v.saved_at).toLocaleString() : "—"}{v.saved_by ? ` · ${v.saved_by}` : ""}</span>
                  <Button size="sm" variant="outline" className="h-6 text-[11px] shrink-0" onClick={() => restoreVersion(v.index)} data-testid={`restore-version-${v.index}`}>Restore</Button>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="bikepath-save">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save bike path</Button>
        </div>
        {editing.id && (
          <ZoneTwoLineCreate
            bikePath={{ id: editing.id, boundary: editing.boundary }}
            zones={pathZones}
            open={splitOpen}
            onOpenChange={setSplitOpen}
            onDone={() => fetchPathZones(editing.id)}
          />
        )}
        </>)}
        {editorTab === "content" && (editing.id
          ? <BikePathContentTab pathId={editing.id} content={editing.content || {}} onSaved={(d) => { setEditing((s) => ({ ...s, content: d.content || {} })); load(); }} />
          : <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center text-muted-foreground" data-testid="content-save-first">Save this bike path first, then add rich content & media here.</div>)}
        {editorTab === "routes" && (editing.id
          ? <RoutesManager embedBikePathId={editing.id} embedState={classifyBoundary(editing.boundary || []).state} embedPath={editing} />
          : <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center text-muted-foreground" data-testid="routes-save-first">Save this bike path first, then add its routes here.</div>)}
        {editorTab === "stops" && (editing.id
          ? <ContentManager mode="global" embedBikePathId={editing.id} />
          : <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center text-muted-foreground" data-testid="stops-save-first">Save this bike path first, then add its stops here.</div>)}
        {editorTab === "assignments" && (editing.id
          ? <ZonesPanel embedBikePathId={editing.id} />
          : <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center text-muted-foreground" data-testid="assignments-save-first">Save this bike path first, then manage zone assignments here.</div>)}
        {editorTab === "businesses" && (editing.id ? (
          <div className="space-y-3" data-testid="bikepath-businesses-tab">
            <p className="text-sm text-muted-foreground">Local businesses discovered along this path (OpenStreetMap). Toggle layers to scout prospects and sponsors.</p>
            <div className="relative rounded-2xl overflow-hidden border border-border">
              <LeafletMap height={620} center={pts.length ? pts[0] : [41.75, -71.3]}>
                {pts.length >= 3 && <Polygon positions={pts} pathOptions={{ color: "#334155", weight: 2, fillOpacity: 0.04 }} />}
                {routeLines.map((ln, i) => <Polyline key={`r-${i}`} positions={ln} pathOptions={{ color: "#0ea5e9", weight: 5, opacity: 0.95 }} />)}
                {pts.length >= 2 && <FitBounds bounds={pts} />}
                {biz.visibleBiz.length > 0 && <BusinessLayers businesses={biz.visibleBiz} onShopAction={biz.handleShopAction} />}
                {biz.heatOn && <HeatLayer points={biz.heatPoints} enabled={biz.heatOn} />}
              </LeafletMap>
              {pts.length >= 3 && <BusinessLayerToggles {...biz} />}
            </div>
          </div>
        ) : <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center text-muted-foreground" data-testid="businesses-save-first">Save this bike path first, then scout businesses here.</div>)}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-lg font-bold">Bike paths</h2>
          <p className="text-sm text-muted-foreground">Draw path boundaries and manage the territories inside them.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant={needsZone ? "default" : "outline"} onClick={toggleNeedsZone} disabled={coverageLoading} data-testid="needs-zone-btn" title="Show bike paths with less than 50% of their corridor covered by zones">
            {coverageLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <MapPin className="w-4 h-4 mr-1" />}Needs Zone{needsZone ? ` (${filtered.length})` : ""}
          </Button>
          <Button variant="outline" onClick={recomputeAll} disabled={bulk?.status === "running"} data-testid="recompute-all-btn">
            {bulk?.status === "running" ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Recomputing {bulk.done}/{bulk.total}</> : <><RefreshCw className="w-4 h-4 mr-1" />Recompute all</>}
          </Button>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="h-9 w-52 text-xs" data-testid="bikepath-sort-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="name">Sort: Name (A–Z)</SelectItem>
              <SelectItem value="restaurants">Sort: Restaurants</SelectItem>
              <SelectItem value="restaurants_per_mile">Sort: Restaurants / mile</SelectItem>
              <SelectItem value="bike_shops">Sort: Bike shops</SelectItem>
              <SelectItem value="bike_shops_per_mile">Sort: Bike shops / mile</SelectItem>
              <SelectItem value="coffee">Sort: Coffee shops</SelectItem>
              <SelectItem value="breweries">Sort: Breweries</SelectItem>
              <SelectItem value="hotels">Sort: Hotels</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => setDupOpen(true)} data-testid="bikepath-duplicates-button"><Trash2 className="w-4 h-4 mr-1" />Remove duplicates</Button>
          <BikePathDuplicatesDialog open={dupOpen} onOpenChange={setDupOpen} onDone={load} />
          <Button onClick={() => setEditing({ ...EMPTY })} data-testid="new-bikepath-button"><Plus className="w-4 h-4 mr-1" />New bike path</Button>
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-2 rounded-xl border border-border bg-card p-3" data-testid="bikepath-filter-bar">
        <div className="flex flex-col gap-1">
          <Label className="text-xs text-muted-foreground">State</Label>
          <Select value={fState} onValueChange={(v) => { setFState(v); setFCounty("all"); setFName("all"); }}>
            <SelectTrigger className="h-9 w-48 text-xs" data-testid="bikepath-filter-state"><SelectValue placeholder="All states" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">All states</SelectItem>
              {stateOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-xs text-muted-foreground">County</Label>
          <Select value={fCounty} onValueChange={(v) => { setFCounty(v); setFName("all"); }} disabled={fState === "all"}>
            <SelectTrigger className="h-9 w-48 text-xs" data-testid="bikepath-filter-county"><SelectValue placeholder="All counties" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">All counties</SelectItem>
              {countyOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-xs text-muted-foreground">Path name</Label>
          <Select value={fName} onValueChange={setFName}>
            <SelectTrigger className="h-9 w-56 text-xs" data-testid="bikepath-filter-name"><SelectValue placeholder="All paths" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">All paths</SelectItem>
              {nameOptions.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {(fState !== "all" || fCounty !== "all" || fName !== "all") && (
          <Button variant="ghost" size="sm" className="h-9" onClick={() => { setFState("all"); setFCounty("all"); setFName("all"); }} data-testid="bikepath-filter-clear"><X className="w-4 h-4 mr-1" />Clear</Button>
        )}
      </div>
      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : paths.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-12 text-center text-muted-foreground" data-testid="bikepath-empty">No bike paths yet. Create your first one to start defining zones.</div>
      ) : sorted.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-12 text-center text-muted-foreground" data-testid="bikepath-no-matches">{needsZone ? "Every bike path matching these filters is at least 50% covered by zones." : "No bike paths match these filters."}</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {sorted.map((p) => (
            <div key={p.id} className="rounded-2xl border border-border bg-card overflow-hidden" data-testid={`bikepath-card-${p.id}`}>
              <div className="h-32">
                <LeafletMap height={128} center={p.boundary?.length ? p.boundary[0] : [41.75, -71.3]} zoom={11}>
                  {p.boundary?.length >= 3 && <Polygon positions={p.boundary} pathOptions={{ color: "#10B981", fillOpacity: 0.15 }} />}
                  {(p.lines?.length ? p.lines : (p.centerline?.length >= 2 ? [p.centerline] : [])).map((ln, i) => (ln?.length >= 2 ? <Polyline key={`cr-${i}`} positions={ln} pathOptions={{ color: "#0ea5e9", weight: 3, opacity: 0.95 }} /> : null))}
                  {p.boundary?.length >= 2 && <FitBounds bounds={p.boundary} />}
                </LeafletMap>
              </div>
              <div className="p-4">
                <div className="font-heading font-bold flex items-center gap-2">{p.name}
                  {p.status === "draft" && <Badge className="bg-amber-500/15 text-amber-300 border border-amber-500/30 text-[10px] px-1.5 py-0" data-testid={`bikepath-draft-badge-${p.id}`}>Draft</Badge>}
                </div>
                {p.description && <div className="text-sm text-muted-foreground mt-0.5">{p.description}</div>}
                {p.geo?.state && <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1" data-testid={`bikepath-location-${p.id}`}><MapPin className="w-3 h-3 shrink-0" />{p.geo.county ? `${p.geo.county} County · ` : ""}{p.geo.state}</div>}
                <div className="text-xs text-muted-foreground mt-2 flex items-center gap-2">{p.zone_count} zone{p.zone_count === 1 ? "" : "s"}
                  {coverage && coverage[p.id] !== undefined && <span className={`rounded px-1.5 py-0.5 text-[10px] border ${coverage[p.id] < 50 ? "bg-red-500/10 text-red-300 border-red-500/30" : "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"}`} data-testid={`bikepath-coverage-${p.id}`}>{coverage[p.id]}% zoned</span>}
                </div>
                {(p.business_metrics || p.saved_counts) && (
                  <div className="flex flex-wrap gap-1 mt-2 text-[10px]" data-testid={`bikepath-metrics-${p.id}`} title="Saved prospects on this path (same records as the zone cards and Prospects page)">
                    <span className="inline-flex items-center gap-0.5 rounded bg-orange-500/10 text-orange-300 border border-orange-500/20 px-1.5 py-0.5" title="Saved restaurants" data-testid={`bikepath-saved-restaurants-${p.id}`}><Utensils className="w-3 h-3" />{p.saved_counts?.restaurant ?? 0}</span>
                    <span className="inline-flex items-center gap-0.5 rounded bg-cyan-500/10 text-cyan-300 border border-cyan-500/20 px-1.5 py-0.5" title="Saved bike shops"><Bike className="w-3 h-3" />{p.saved_counts?.bike_shop ?? 0}</span>
                    <span className="inline-flex items-center gap-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/20 px-1.5 py-0.5" title="Saved coffee shops"><Coffee className="w-3 h-3" />{p.saved_counts?.coffee ?? 0}</span>
                    <span className="inline-flex items-center gap-0.5 rounded bg-violet-500/10 text-violet-300 border border-violet-500/20 px-1.5 py-0.5" title="Saved breweries"><Beer className="w-3 h-3" />{p.saved_counts?.brewery ?? 0}</span>
                    <span className="inline-flex items-center gap-0.5 rounded bg-blue-500/10 text-blue-300 border border-blue-500/20 px-1.5 py-0.5" title="Saved hotels"><Hotel className="w-3 h-3" />{p.saved_counts?.hotel ?? 0}</span>
                    <span className="inline-flex items-center gap-0.5 rounded bg-muted text-muted-foreground border border-border px-1.5 py-0.5" title="Corridor width"><Ruler className="w-3 h-3" />{p.business_metrics?.corridor_width_miles ?? 1}mi</span>
                  </div>
                )}
                <div className="flex gap-2 mt-3">
                  {p.status === "draft" && <Button size="sm" onClick={() => publish(p.id)} data-testid={`publish-bikepath-${p.id}`}><CheckCircle2 className="w-4 h-4 mr-1" />Publish</Button>}
                  <Button size="sm" variant="outline" onClick={() => openEdit(p.id)} disabled={openingId === p.id} data-testid={`edit-bikepath-${p.id}`}>{openingId === p.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Pencil className="w-4 h-4 mr-1" />}Edit</Button>
                  {!p.trail_repository_id && (
                    <Button size="sm" variant="outline" onClick={() => openAttach(p)} data-testid={`attach-centerline-${p.id}`} title="Attach a real harvested trail centerline — replaces a hand-drawn/straight line with the true blue route on the rider map"><Spline className="w-4 h-4 mr-1" />Attach centerline</Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => refreshAmenities(p.id)} disabled={refreshingAmen[p.id]} data-testid={`refresh-amenities-${p.id}`} title="Auto-populate bathrooms, parking & parks from OpenStreetMap"><MapPin className="w-4 h-4 mr-1" />{refreshingAmen[p.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : "Amenities"}</Button>
                  {p.trail_repository_id && (
                    <Button size="sm" variant="outline" onClick={() => recomputeMetrics(p.id)} disabled={recomputing[p.id]} data-testid={`recompute-metrics-${p.id}`}>
                      {recomputing[p.id] ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}Recompute
                    </Button>
                  )}
                  <AlertDialog>
                    <AlertDialogTrigger asChild><Button size="sm" variant="ghost" className="text-destructive" data-testid={`delete-bikepath-${p.id}`}><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader><AlertDialogTitle>Delete "{p.name}"?</AlertDialogTitle><AlertDialogDescription>This also deletes all {p.zone_count} zone(s) inside it. This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => del(p.id)} data-testid={`confirm-delete-bikepath-${p.id}`}>Delete</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!attachFor} onOpenChange={(o) => { if (!o) setAttachFor(null); }}>
        <DialogContent className="max-w-2xl" data-testid="attach-centerline-dialog">
          <DialogHeader>
            <DialogTitle>Attach centerline to "{attachFor?.name}"</DialogTitle>
            <DialogDescription>Find the matching trail from the national repository and attach its real centerline. This only updates the map line — the boundary, zones, attractions and routes stay exactly as they are.</DialogDescription>
          </DialogHeader>
          <div className="flex items-end gap-2">
            <div className="flex flex-col gap-1">
              <Label className="text-xs text-muted-foreground">State</Label>
              <Select value={attachState} onValueChange={setAttachState}>
                <SelectTrigger className="h-9 w-40 text-xs" data-testid="attach-state-select"><SelectValue placeholder="Pick a state" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {attachStates.map((s) => <SelectItem key={s.state} value={s.state}>{s.name} ({s.available})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 flex flex-col gap-1">
              <Label className="text-xs text-muted-foreground">Trail name</Label>
              <Input value={attachQ} onChange={(e) => setAttachQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && searchTrails()} placeholder="e.g. Air Line Trail" data-testid="attach-search-input" className="h-9" />
            </div>
            <Button onClick={searchTrails} disabled={attachLoading} data-testid="attach-search-btn" className="h-9">{attachLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}</Button>
          </div>
          <div className="max-h-80 overflow-auto rounded-lg border border-border divide-y divide-border" data-testid="attach-results">
            {attachResults.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">{attachLoading ? "Searching…" : "Search for a trail to attach its centerline."}</div>
            ) : attachResults.map((t) => (
              <div key={t.id} className="flex items-center justify-between gap-3 p-3" data-testid={`attach-trail-${t.id}`}>
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate flex items-center gap-1.5">{t.status === "saved" && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" title="Already saved" />}{t.name}</div>
                  <div className="text-xs text-muted-foreground">{(t.length_miles ?? 0)} mi · {t.surface_class || t.surface_type || "unknown"}</div>
                </div>
                <Button size="sm" onClick={() => doAttach(t.id)} disabled={!!attaching} data-testid={`attach-confirm-${t.id}`} className="shrink-0">{attaching === t.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Spline className="w-4 h-4 mr-1" />Attach</>}</Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
